import dayjs from 'dayjs';

import { useTaxDocuments } from '@/api/taxDocuments';
import type { RevenueRow } from '@/api/reports';
import { EmptyState } from '@/components/PageHeading';
import { formatAmount } from '@core/format/money';
import { Dialog } from '../bookings/Dialog';

/** The register asks for this many at once; a busier period says how many more there are. */
const pageSize = 200;

/**
 * One row of the rental revenue register opened: its invoices, from the invoice register's own read
 * (`GET /api/tax-document`, issued only, the row's dates) — the report adds no read of its own for
 * this. A credit note is marked; the figures beside each are the document's own.
 */
export function RevenueInvoicesDialog({
  branchId,
  row,
  label,
  onClose,
}: {
  branchId: number | undefined;
  row: RevenueRow;
  label: string;
  onClose: () => void;
}) {
  const invoices = useTaxDocuments(branchId, {
    status: 'Issued',
    from: row.from,
    to: row.to,
    pageSize,
  });

  const items = invoices.data?.items ?? [];
  const total = invoices.data?.total ?? 0;

  return (
    <Dialog
      title={`${label} — invoices`}
      subtitle={
        invoices.data
          ? `${total} ${total === 1 ? 'document' : 'documents'} · ₹${formatAmount(row.earned)} before GST`
          : undefined
      }
      width={760}
      onClose={onClose}
    >
      {invoices.isLoading ? (
        <p className="text-default mb-0 text-xs">Reading the invoices…</p>
      ) : items.length === 0 ? (
        <EmptyState icon="icon icon-receipt" title="No invoices in this period" />
      ) : (
        <>
          <ul className="m-0 flex flex-col ps-0">
            {items.map((invoice) => (
              <li
                key={invoice.id}
                className="border-border-color mb-0 flex list-none items-start justify-between gap-3 border-b py-2.5 last:border-b-0"
              >
                <span className="min-w-0">
                  <span className="block text-xs font-semibold text-gray-900">
                    <span className="font-mono-nums">{invoice.documentNumber}</span>
                    {invoice.kind !== 'Invoice' && (
                      <span className="text-warning ms-2 text-[11px] font-semibold">
                        {invoice.kind === 'CreditNote' ? 'Credit note' : 'Debit note'}
                      </span>
                    )}
                  </span>
                  <span className="text-default block text-[11px]">
                    {dayjs(invoice.documentDate).format('D MMM YYYY')} · {invoice.customerName} ·{' '}
                    {invoice.bookingNumber}
                  </span>
                </span>
                <span className="flex shrink-0 flex-col items-end">
                  <span className="font-mono-nums text-xs font-bold text-gray-900">
                    ₹{formatAmount(invoice.documentTotal)}
                  </span>
                  <span className="text-default text-[11px]">
                    ₹{formatAmount(invoice.taxableValue)} before GST
                  </span>
                </span>
              </li>
            ))}
          </ul>
          {total > items.length && (
            <p className="text-default mb-0 text-[11px]">
              The first {items.length} of {total} — open Rental invoices for the rest.
            </p>
          )}
        </>
      )}
    </Dialog>
  );
}
